import type {Component, PanelLifecycleContext} from 'panel';

import type {QueryEntrySection} from 'common/query-builder/query-entry-section';
import type {Entry} from 'common/query-builder/query-entry-section/types';
import {Subscribable} from 'common/types/context/subscribable';

export class DragAndDropManager implements PanelLifecycleContext, Subscribable {
  private declare _draggingEl: QueryEntrySection | null;
  private declare _draggingEntry: Entry | null;
  private connectedComponents: Set<Component<any>> = new Set();
  private dragoverEls: Set<HTMLElement> = new Set();
  private subscriptions: Array<() => void> = [];

  constructor() {
    this._draggingEl = null;
    this._draggingEntry = null;
  }

  public subscribe(callback: () => void): this {
    this.subscriptions.push(callback);
    return this;
  }

  public unsubscribe(callback: () => void): this {
    this.subscriptions = this.subscriptions.filter((subscription) => subscription !== callback);
    return this;
  }

  get draggingEntry() {
    return this._draggingEntry;
  }

  get draggingEl() {
    return this._draggingEl;
  }

  public clearDraggingInfo() {
    this._draggingEl = null;
    this._draggingEntry = null;
    this.updateComponents();
  }

  public isDraggingOverChild(el: HTMLElement) {
    // returns true if we are dragging over a child - note this only works one nesting deep for now since it's not possible to nest further
    return Array.from(this.dragoverEls).some((dragoverEl) => el !== dragoverEl && el.contains(dragoverEl));
  }

  public addDragoverEl(dragoverEl: HTMLElement) {
    this.dragoverEls.add(dragoverEl);
    this.updateComponents();
  }

  public removeDragoverEl(dragoverEl: HTMLElement) {
    this.dragoverEls.delete(dragoverEl);
    this.updateComponents();
  }

  public setDraggingInfo(draggingEl: QueryEntrySection, draggingEntry: Entry) {
    this._draggingEl = draggingEl;
    this._draggingEntry = draggingEl && draggingEntry;
    this.updateComponents();
  }

  // Panel Context lifecycle methods
  public bindToComponent(component: Component<any, any, any, any, any>): void {
    this.connectedComponents.add(component);
  }
  public unbindFromComponent(component: Component<any, any, any, any, any>): void {
    this.connectedComponents.delete(component);
  }

  private updateComponents() {
    for (const component of this.connectedComponents) {
      component.update();
    }
    for (const subscription of this.subscriptions) {
      subscription();
    }
  }
}
